import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { onboardingApi, type OnboardingState } from '../../helpers/OnboardingApi'
import { getApiErrorMessage } from '../../helpers/ApiErrorMessage'
import { useAuth } from '../AuthContext'

interface OnboardingContextData {
  state: OnboardingState | null
  isLoading: boolean
  error: string
  reload: () => Promise<void>
  update: (state: OnboardingState) => void
}
const OnboardingContext = createContext<OnboardingContextData | null>(null)

export function OnboardingProvider ({ children }: { children: ReactNode }): React.ReactElement {
  const { user } = useAuth()
  const token = window.localStorage.getItem('sessionToken')
  const userId = user?.id
  const identity = token ? `${String(userId ?? '')}:${token}` : ''
  const [state, setState] = useState<OnboardingState | null>(null)
  const [isLoading, setIsLoading] = useState(Boolean(token))
  const [error, setError] = useState('')
  const [loadedIdentity, setLoadedIdentity] = useState('')
  const revision = useRef(0)

  const reload = useCallback(async () => {
    const requestId = ++revision.current
    if (!token) { setState(null); setIsLoading(false); setError(''); setLoadedIdentity(''); return }
    setIsLoading(true)
    setError('')
    try {
      const next = await onboardingApi.getState()
      if (requestId === revision.current) { setState(next); setLoadedIdentity(identity) }
    } catch (error) {
      if (requestId === revision.current) { setError(getApiErrorMessage(error, 'Não foi possível carregar sua configuração.')); setLoadedIdentity(identity) }
    } finally {
      if (requestId === revision.current) setIsLoading(false)
    }
  }, [token, identity])

  const update = useCallback((next: OnboardingState) => {
    ++revision.current
    setState(next)
    setIsLoading(false)
    setError('')
    setLoadedIdentity(identity)
  }, [identity])

  useEffect(() => {
    void reload()
    return () => { ++revision.current }
  }, [reload])

  const isCurrentIdentity = identity === loadedIdentity
  return <OnboardingContext.Provider value={{ state: isCurrentIdentity ? state : null, isLoading: Boolean(token) && (!isCurrentIdentity || isLoading), error: isCurrentIdentity ? error : '', reload, update }}>{children}</OnboardingContext.Provider>
}

export function useOnboarding (): OnboardingContextData {
  const value = useContext(OnboardingContext)
  if (!value) throw new Error('OnboardingProvider não encontrado')
  return value
}
